import React, { useState } from 'react';
import {
  Activity,
  Calendar,
  FileText,
  StickyNote,
  MessageSquare,
  ArrowRightLeft,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useFetchClientActivityFeed } from '@/hooks/useClients';
import type { ActivityEventType } from '@/types/client';
import { formatDistanceToNow, format, isAfter, subDays } from 'date-fns';

interface ActivityTabProps {
  clientId: string;
}

const EVENT_ICONS: Record<ActivityEventType, React.ReactNode> = {
  booking: <Calendar className="h-4 w-4" />,
  invoice: <FileText className="h-4 w-4" />,
  note: <StickyNote className="h-4 w-4" />,
  communication: <MessageSquare className="h-4 w-4" />,
  stage_change: <ArrowRightLeft className="h-4 w-4" />,
};

const EVENT_COLORS: Record<ActivityEventType, string> = {
  booking: 'text-led-blue',
  invoice: 'text-led-purple',
  note: 'text-led-amber',
  communication: 'text-led-green',
  stage_change: 'text-led-cyan',
};

const EVENT_LABELS: Record<ActivityEventType, string> = {
  booking: 'Booking',
  invoice: 'Invoice',
  note: 'Note',
  communication: 'Comms',
  stage_change: 'Stage',
};

const ALL_EVENT_TYPES: ActivityEventType[] = ['booking', 'invoice', 'note', 'communication', 'stage_change'];

function formatEventDate(dateStr: string): string {
  const date = new Date(dateStr);
  const sevenDaysAgo = subDays(new Date(), 7);
  if (isAfter(date, sevenDaysAgo)) {
    return formatDistanceToNow(date, { addSuffix: true });
  }
  return format(date, 'MMM d, yyyy');
}

const PAGE_SIZE = 50;

export const ActivityTab: React.FC<ActivityTabProps> = ({ clientId }) => {
  const [activeFilters, setActiveFilters] = useState<Set<ActivityEventType>>(new Set());
  const [displayCount, setDisplayCount] = useState(PAGE_SIZE);

  const filterTypes = activeFilters.size > 0 ? Array.from(activeFilters) : undefined;
  const { data: feed = [], isLoading, error } = useFetchClientActivityFeed(clientId, {
    eventTypes: filterTypes,
  });

  const visibleFeed = feed.slice(0, displayCount);
  const hasMore = feed.length > displayCount;

  const toggleFilter = (type: ActivityEventType) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return <p className="text-sm text-destructive py-4">Failed to load activity feed.</p>;
  }

  return (
    <div className="space-y-4">
      {/* Filter pills */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-none">
        {ALL_EVENT_TYPES.map((type) => {
          const isActive = activeFilters.has(type);
          return (
            <button
              key={type}
              onClick={() => toggleFilter(type)}
              aria-pressed={isActive}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-label uppercase tracking-wider whitespace-nowrap transition-colors border',
                isActive
                  ? `${EVENT_COLORS[type]} bg-current/10 border-current/20`
                  : 'text-muted-foreground bg-surface-2 border-border hover:bg-surface-3'
              )}
            >
              {EVENT_ICONS[type]}
              {EVENT_LABELS[type]}
            </button>
          );
        })}
      </div>

      {/* Timeline */}
      {feed.length === 0 ? (
        <div className="text-center py-8 space-y-1">
          <Activity className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">
            {activeFilters.size > 0 ? 'No matching activity for these filters.' : 'No activity recorded yet.'}
          </p>
          {activeFilters.size === 0 && (
            <p className="text-xs text-muted-foreground">Bookings, notes, and communications will show up here.</p>
          )}
        </div>
      ) : (
        <div className="space-y-1">
          {visibleFeed.map((item, i) => (
            <div
              key={`${item.event_type}-${item.event_date}-${i}`}
              className="flex items-start gap-3 py-2.5 px-3 bg-surface-1 border border-border rounded"
            >
              <div className={cn('mt-0.5 flex-shrink-0', EVENT_COLORS[item.event_type])}>
                {EVENT_ICONS[item.event_type]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground break-words">{item.summary}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {formatEventDate(item.event_date)}
                </p>
              </div>
            </div>
          ))}
          {hasMore && (
            <button
              onClick={() => setDisplayCount((c) => c + PAGE_SIZE)}
              className="w-full py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              Load more ({feed.length - displayCount} remaining)
            </button>
          )}
        </div>
      )}
    </div>
  );
};
